import { useState, useEffect } from 'react';
import { supabase } from '../services/supabase';
import { UserProfile } from '../types/UserProfile';
import { User } from '@supabase/supabase-js';

interface UseUserProfileResult {
  user: User | null;
  profile: UserProfile | null;
  loading: boolean;
}

export function useUserProfile(): UseUserProfileResult {
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const {
          data: { session },
        } = await supabase.auth.getSession();

        if (!session?.user) {
          setUser(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        setUser(session.user);

        const { data, error } = await supabase
          .from('profiles')
          .select(
            `
            id,
            first_name,
            last_name,
            birthday,
            gender,
            political_party,
            created_at,
            updated_at,
            street_address,
            city,
            state,
            postal_code,
            country,
            phone,
            employment_status,
            occupation,
            employer,
            state_code,
            county_name,
            congressional_district,
            metro_area,
            zip_validated,
            preferred_org:preferred_org_id (
              id,
              name,
              description,
              category,
              logo,
              recipient_type,
              fec_committee_id,
              donation_provider,
              donation_url,
              slug,
              org_type,
              party_family,
              level,
              state_code,
              website_url,
              logo_url,
              is_active,
              is_verified,
              display_order,
              created_at,
              updated_at
            )
          `
          )
          .eq('id', session.user.id)
          .maybeSingle();

        if (error) {
          console.error('Error fetching profile:', error);
          setProfile(null);
          return;
        }

        if (!data) {
          setProfile(null);
          return;
        }

        const preferredOrg = Array.isArray(data.preferred_org)
          ? data.preferred_org[0] ?? null
          : data.preferred_org ?? null;

        const profileData: UserProfile = {
          id: data.id,
          first_name: data.first_name,
          last_name: data.last_name,
          birthday: data.birthday,
          gender: data.gender,
          political_party: data.political_party,
          created_at: data.created_at,
          updated_at: data.updated_at,
          preferred_org: preferredOrg
            ? {
                id: preferredOrg.id,
                name: preferredOrg.name,
                description: preferredOrg.description ?? null,
                category: preferredOrg.category ?? null,
                logo: preferredOrg.logo ?? null,
                recipient_type: preferredOrg.recipient_type ?? null,
                fec_committee_id: preferredOrg.fec_committee_id ?? null,
                donation_provider: preferredOrg.donation_provider ?? null,
                donation_url: preferredOrg.donation_url ?? null,
                slug: preferredOrg.slug ?? null,
                org_type: preferredOrg.org_type ?? 'party',
                party_family: preferredOrg.party_family ?? null,
                level: preferredOrg.level ?? 'national',
                state_code: preferredOrg.state_code ?? null,
                website_url: preferredOrg.website_url ?? null,
                logo_url: preferredOrg.logo_url ?? preferredOrg.logo ?? null,
                is_active: preferredOrg.is_active ?? true,
                is_verified: preferredOrg.is_verified ?? false,
                display_order: preferredOrg.display_order ?? 100,
                created_at: preferredOrg.created_at ?? undefined,
                updated_at: preferredOrg.updated_at ?? undefined,
              }
            : null,
          street_address: data.street_address,
          city: data.city,
          state: data.state,
          postal_code: data.postal_code,
          country: data.country,
          phone: data.phone,
          employment_status: data.employment_status,
          occupation: data.occupation,
          employer: data.employer,
          state_code: data.state_code,
          county_name: data.county_name,
          congressional_district: data.congressional_district,
          metro_area: data.metro_area,
          zip_validated: data.zip_validated,
        };

        setProfile(profileData);
      } catch (error) {
        console.error('Unexpected error fetching profile:', error);
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, []);

  return { user, profile, loading };
}